import { useTranslation } from 'react-i18next';
import { LandingHeader } from '@/modules/landing/components/landing-header';
import { LandingFooter } from '@/modules/landing/components/landing-footer';
import { SeoHead } from '@/components/core';

const SubSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="mt-5">
    <h3 className="text-base font-semibold text-oxford-blue font-aptos">
      {title}
    </h3>
    <div className="mt-2 text-base text-eerie-black/80 font-open-sans leading-relaxed">
      {children}
    </div>
  </div>
);

export const TermsOfServicePage = () => {
  const { t } = useTranslation();

  return (
    <main className="min-h-screen w-full flex flex-col">
      <SeoHead
        title="Terms of Service — SELISE Legal Templates"
        description="Terms of Service for SELISE Legal Templates. Read our terms carefully before using our AI-powered legal document creation service."
        canonicalPath="/terms-of-service"
      />
      <LandingHeader />

      <article className="flex-1 bg-white">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:px-8 lg:py-24">
          {/* Title */}
          <h1 className="text-4xl font-bold tracking-tight text-oxford-blue font-aptos">
            {t('TOS_TITLE')}
          </h1>
          <p className="mt-4 text-lg text-globe-grey font-open-sans leading-relaxed">
            {t('TOS_SUBTITLE')}
          </p>

          {/* Important Notice */}
          <div className="mt-8 rounded-lg border border-crimson/20 bg-crimson/5 p-6">
            <p className="text-sm font-semibold text-crimson leading-relaxed">
              {t('TOS_IMPORTANT_NOTICE')}
            </p>
          </div>

          {/* Sections */}
          <div className="mt-12 space-y-10">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S1_TITLE')}
              </h2>
              <p className="mt-4 text-base text-eerie-black/80 font-open-sans leading-relaxed">
                {t('TOS_S1_P1')}
              </p>
              <p className="mt-3 text-base text-eerie-black/80 font-open-sans leading-relaxed">
                {t('TOS_S1_P2')}
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S2_TITLE')}
              </h2>
              <SubSection title={t('TOS_S2_1_TITLE')}>
                <p>{t('TOS_S2_1_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S2_2_TITLE')}>
                <p>{t('TOS_S2_2_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S2_3_TITLE')}>
                <p>{t('TOS_S2_3_CONTENT')}</p>
              </SubSection>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S3_TITLE')}
              </h2>
              <SubSection title={t('TOS_S3_1_TITLE')}>
                <p>{t('TOS_S3_1_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S3_2_TITLE')}>
                <p>{t('TOS_S3_2_CONTENT')}</p>
              </SubSection>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S4_TITLE')}
              </h2>
              <SubSection title={t('TOS_S4_1_TITLE')}>
                <p>{t('TOS_S4_1_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S4_2_TITLE')}>
                <p>{t('TOS_S4_2_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S4_3_TITLE')}>
                <p>{t('TOS_S4_3_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S4_4_TITLE')}>
                <p>{t('TOS_S4_4_CONTENT')}</p>
              </SubSection>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S5_TITLE')}
              </h2>
              <SubSection title={t('TOS_S5_1_TITLE')}>
                <p>{t('TOS_S5_1_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S5_2_TITLE')}>
                <p>{t('TOS_S5_2_CONTENT')}</p>
              </SubSection>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S6_TITLE')}
              </h2>
              <SubSection title={t('TOS_S6_1_TITLE')}>
                <p className="uppercase">{t('TOS_S6_1_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S6_2_TITLE')}>
                <p className="uppercase">{t('TOS_S6_2_CONTENT')}</p>
              </SubSection>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S7_TITLE')}
              </h2>
              <SubSection title={t('TOS_S7_1_TITLE')}>
                <p>{t('TOS_S7_1_CONTENT')}</p>
              </SubSection>
              <SubSection title={t('TOS_S7_2_TITLE')}>
                <p>{t('TOS_S7_2_CONTENT')}</p>
              </SubSection>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S8_TITLE')}
              </h2>
              <p className="mt-4 text-base text-eerie-black/80 font-open-sans leading-relaxed">
                {t('TOS_S8_CONTENT')}
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-semibold text-oxford-blue font-aptos">
                {t('TOS_S9_TITLE')}
              </h2>
              <p className="mt-4 text-base text-eerie-black/80 font-open-sans leading-relaxed">
                {t('TOS_S9_INTRO')}
              </p>
              <address className="mt-4 not-italic text-base text-eerie-black/80 font-open-sans leading-relaxed">
                <p className="font-semibold">{t('TOS_S9_COMPANY')}</p>
                <p>{t('TOS_S9_ADDRESS_1')}</p>
                <p>{t('TOS_S9_ADDRESS_2')}</p>
                <p>{t('TOS_S9_COUNTRY')}</p>
              </address>
            </section>
          </div>
        </div>
      </article>

      <LandingFooter />
    </main>
  );
};
